import { useEffect, useMemo, useState, type DragEvent, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    getChatGroupOrder,
    getChatGroupPins,
    getChatGroupStats,
    getFriends,
    getGroups,
    markChatNotificationsRead,
    openDirectGroup,
    searchChatMessages,
    setChatGroupOrder,
    setChatGroupPin,
} from '@/lib/api';
import { MessageSquarePlus, Pin, Users, X } from 'lucide-react';

interface GroupListProps {
    onSelectGroup: (group: any) => void;
    selectedGroupId?: number;
    notifications?: any;
    onRefreshNotifications?: () => void;
    onJumpToMessage?: (groupId: number, messageId: number) => void;
}

const resolveDisplayName = (user: any) => {
    const displayName = String(user?.display_name || '').trim();
    if (displayName) return displayName;
    const username = String(user?.username || '').trim();
    if (username) return username;
    const roomNo = String(user?.room_no || '').trim();
    if (roomNo) return `RoomNo.${roomNo}`;
    return '住人';
};

export function GroupList({ onSelectGroup, selectedGroupId, notifications, onRefreshNotifications, onJumpToMessage }: GroupListProps) {
    const navigate = useNavigate();
    const [groups, setGroups] = useState<any[]>([]);
    const [expandedMentions, setExpandedMentions] = useState<Record<number, boolean>>({});
    const [expandedReplies, setExpandedReplies] = useState<Record<number, boolean>>({});
    const [groupStats, setGroupStats] = useState<Record<number, any>>({});
    const [groupOrder, setGroupOrderState] = useState<number[]>([]);
    const [pinnedGroupIds, setPinnedGroupIds] = useState<number[]>([]);
    const [orderTab, setOrderTab] = useState<'fixed' | 'recent'>('fixed');
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [searching, setSearching] = useState(false);
    const [draggingId, setDraggingId] = useState<number | null>(null);
    const [dragOverId, setDragOverId] = useState<number | null>(null);
    const [directModalOpen, setDirectModalOpen] = useState(false);
    const [friends, setFriends] = useState<any[]>([]);
    const [friendsLoading, setFriendsLoading] = useState(false);
    const [friendSearch, setFriendSearch] = useState('');
    const [directStartingId, setDirectStartingId] = useState<number | null>(null);

    const fetchGroups = async () => {
        try {
            const res = await getGroups();
            setGroups(res.data || []);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchGroupStats = async () => {
        try {
            const res = await getChatGroupStats();
            setGroupStats(res.data?.groups || {});
        } catch (err) {
            console.error(err);
        }
    };

    const fetchGroupOrder = async () => {
        try {
            const res = await getChatGroupOrder();
            setGroupOrderState(res.data?.group_ids || []);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchPinnedGroups = async () => {
        try {
            const res = await getChatGroupPins();
            setPinnedGroupIds(Array.isArray(res.data?.group_ids) ? res.data.group_ids.map((id: number) => Number(id)) : []);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchFriendsList = async () => {
        setFriendsLoading(true);
        try {
            const res = await getFriends();
            setFriends(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error(err);
            setFriends([]);
        } finally {
            setFriendsLoading(false);
        }
    };

    useEffect(() => {
        fetchGroups();
        fetchGroupStats();
        fetchGroupOrder();
        fetchPinnedGroups();

        const interval = setInterval(() => {
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
            fetchGroupStats();
        }, 30000);
        const handleRead = () => fetchGroupStats();
        const handleVisibility = () => {
            if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
                fetchGroupStats();
            }
        };
        const handlePinChanged = (event: Event) => {
            const detail = (event as CustomEvent<{ groupId?: number; pinned?: boolean }>).detail;
            if (!detail || !Number.isFinite(Number(detail.groupId))) {
                fetchPinnedGroups();
                return;
            }
            const groupId = Number(detail.groupId);
            const pinned = detail.pinned === true;
            setPinnedGroupIds((prev) => {
                const next = new Set(prev);
                if (pinned) {
                    next.add(groupId);
                } else {
                    next.delete(groupId);
                }
                return Array.from(next);
            });
        };
        window.addEventListener('maison:salon-read', handleRead);
        window.addEventListener('maison:chat-pin-changed', handlePinChanged);
        document.addEventListener('visibilitychange', handleVisibility);
        return () => {
            clearInterval(interval);
            window.removeEventListener('maison:salon-read', handleRead);
            window.removeEventListener('maison:chat-pin-changed', handlePinChanged);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, []);

    useEffect(() => {
        if (!directModalOpen) return;
        fetchFriendsList();
    }, [directModalOpen]);

    const groupNotifications = useMemo(() => {
        const map: Record<number, { mentions: any[]; replies: any[] }> = {};
        if (!notifications?.groups) return map;
        Object.entries(notifications.groups).forEach(([groupId, data]: any) => {
            map[Number(groupId)] = {
                mentions: data.mentions || [],
                replies: data.replies || [],
            };
        });
        return map;
    }, [notifications]);

    useEffect(() => {
        const term = searchTerm.trim();
        if (!term) {
            setSearchResults([]);
            setSearching(false);
            return;
        }
        const handle = setTimeout(async () => {
            setSearching(true);
            try {
                const res = await searchChatMessages(term);
                setSearchResults(res.data?.matches || []);
            } catch (err) {
                console.error(err);
                setSearchResults([]);
            } finally {
                setSearching(false);
            }
        }, 400);
        return () => clearTimeout(handle);
    }, [searchTerm]);

    const handleMarkRead = async (notificationIds: number[]) => {
        if (!notificationIds.length) return;
        try {
            await markChatNotificationsRead({ notification_ids: notificationIds });
            if (onRefreshNotifications) onRefreshNotifications();
        } catch (err) {
            console.error(err);
        }
    };

    const isSearchActive = searchTerm.trim().length > 0;
    const pinnedSet = useMemo(() => new Set(pinnedGroupIds), [pinnedGroupIds]);

    const searchMatchesByGroup = useMemo(() => {
        const map: Record<number, any[]> = {};
        searchResults.forEach((item) => {
            const groupId = Number(item.group_id);
            if (!map[groupId]) {
                map[groupId] = [];
            }
            map[groupId].push(item);
        });
        return map;
    }, [searchResults]);

    const searchGroupOrder = useMemo(() => {
        const order: number[] = [];
        const seen = new Set<number>();
        searchResults.forEach((item) => {
            const groupId = Number(item.group_id);
            if (!seen.has(groupId)) {
                order.push(groupId);
                seen.add(groupId);
            }
        });
        return order;
    }, [searchResults]);

    const resolveSearchName = (displayName?: string | null, roomNo?: string | null) => {
        if (displayName && displayName.trim()) return displayName.trim();
        if (roomNo && roomNo.trim()) return roomNo.trim();
        return '住人';
    };

    const fixedOrderIds = useMemo(() => {
        const base = groupOrder.length ? [...groupOrder] : groups.map((group) => group.group_id);
        const seen = new Set(base);
        groups.forEach((group) => {
            if (!seen.has(group.group_id)) {
                base.push(group.group_id);
                seen.add(group.group_id);
            }
        });
        return base;
    }, [groupOrder, groups]);

    const orderedGroups = useMemo(() => {
        let list = [...groups];
        if (isSearchActive) {
            const matchedIds = new Set(Object.keys(searchMatchesByGroup).map(Number));
            list = list.filter((group) => matchedIds.has(group.group_id));
            const orderIndex = new Map(searchGroupOrder.map((id, index) => [id, index]));
            list.sort((a, b) => {
                const aIndex = orderIndex.get(a.group_id) ?? 0;
                const bIndex = orderIndex.get(b.group_id) ?? 0;
                return aIndex - bIndex;
            });
            return list;
        }

        const orderIndex = new Map(fixedOrderIds.map((id, index) => [id, index]));
        const sortPinnedFirst = (a: any, b: any) => {
            const aPinned = pinnedSet.has(a.group_id) ? 0 : 1;
            const bPinned = pinnedSet.has(b.group_id) ? 0 : 1;
            if (aPinned !== bPinned) return aPinned - bPinned;
            return 0;
        };

        if (orderTab === 'fixed') {
            list.sort((a, b) => {
                const pinDiff = sortPinnedFirst(a, b);
                if (pinDiff !== 0) return pinDiff;
                const aIndex = orderIndex.get(a.group_id) ?? 0;
                const bIndex = orderIndex.get(b.group_id) ?? 0;
                return aIndex - bIndex;
            });
            return list;
        }

        list.sort((a, b) => {
            const pinDiff = sortPinnedFirst(a, b);
            if (pinDiff !== 0) return pinDiff;
            const aStat = groupStats[a.group_id] || {};
            const bStat = groupStats[b.group_id] || {};
            const aUnread = Number(aStat.unread_count || 0) > 0 ? 0 : 1;
            const bUnread = Number(bStat.unread_count || 0) > 0 ? 0 : 1;
            if (aUnread !== bUnread) return aUnread - bUnread;
            const aTimeRaw = aStat.last_message_at ? Date.parse(aStat.last_message_at) : 0;
            const bTimeRaw = bStat.last_message_at ? Date.parse(bStat.last_message_at) : 0;
            const aTime = Number.isFinite(aTimeRaw) ? aTimeRaw : 0;
            const bTime = Number.isFinite(bTimeRaw) ? bTimeRaw : 0;
            if (aTime !== bTime) return bTime - aTime;
            return (orderIndex.get(a.group_id) ?? a.group_id) - (orderIndex.get(b.group_id) ?? b.group_id);
        });
        return list;
    }, [fixedOrderIds, groupStats, groups, isSearchActive, orderTab, pinnedSet, searchGroupOrder, searchMatchesByGroup]);

    const handleReorder = async (sourceId: number, targetId: number) => {
        if (sourceId === targetId) return;
        const base = [...fixedOrderIds];
        const fromIndex = base.indexOf(sourceId);
        const toIndex = base.indexOf(targetId);
        if (fromIndex === -1 || toIndex === -1) return;
        base.splice(fromIndex, 1);
        base.splice(toIndex, 0, sourceId);
        setGroupOrderState(base);
        try {
            await setChatGroupOrder(base);
        } catch (err) {
            console.error(err);
        }
    };

    const handleDragStart = (event: DragEvent<HTMLDivElement>, groupId: number) => {
        if (orderTab !== 'fixed' || isSearchActive) return;
        setDraggingId(groupId);
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(groupId));
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>, groupId: number) => {
        if (orderTab !== 'fixed' || isSearchActive) return;
        event.preventDefault();
        setDragOverId(groupId);
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>, groupId: number) => {
        if (orderTab !== 'fixed' || isSearchActive) return;
        event.preventDefault();
        if (draggingId === null) return;
        handleReorder(draggingId, groupId);
        setDraggingId(null);
        setDragOverId(null);
    };

    const handleDragEnd = () => {
        setDraggingId(null);
        setDragOverId(null);
    };

    const togglePinForGroup = async (groupId: number, nextPinned: boolean) => {
        setPinnedGroupIds((prev) => {
            const next = new Set(prev);
            if (nextPinned) {
                next.add(groupId);
            } else {
                next.delete(groupId);
            }
            return Array.from(next);
        });
        try {
            await setChatGroupPin(groupId, nextPinned);
            window.dispatchEvent(new CustomEvent('maison:chat-pin-changed', { detail: { groupId, pinned: nextPinned } }));
        } catch (err) {
            console.error(err);
            fetchPinnedGroups();
        }
    };

    const filteredFriends = useMemo(() => {
        const term = friendSearch.trim().toLowerCase();
        const sorted = [...friends].sort((a, b) => resolveDisplayName(a).localeCompare(resolveDisplayName(b), 'ja'));
        if (!term) return sorted;
        return sorted.filter((friend) => {
            const display = resolveDisplayName(friend).toLowerCase();
            const roomNo = String(friend?.room_no || '').toLowerCase();
            const username = String(friend?.username || '').toLowerCase();
            return display.includes(term) || roomNo.includes(term) || username.includes(term);
        });
    }, [friendSearch, friends]);

    const handleOpenDirectChat = async (friend: any) => {
        if (!friend?.user_id || directStartingId) return;
        setDirectStartingId(friend.user_id);
        try {
            const res = await openDirectGroup(friend.user_id);
            await Promise.all([fetchGroups(), fetchGroupStats(), fetchGroupOrder(), fetchPinnedGroups()]);
            if (res.data) {
                onSelectGroup(res.data);
            }
            setDirectModalOpen(false);
            setFriendSearch('');
        } catch (err: any) {
            console.error(err);
            alert(err?.response?.data?.detail || '個人チャットを開始できませんでした。');
        } finally {
            setDirectStartingId(null);
        }
    };

    const handleProfileNavigate = (event: MouseEvent<HTMLElement>, userId?: number | null) => {
        if (!userId) return;
        event.stopPropagation();
        navigate(`/users/${userId}`);
    };

    return (
        <div className="w-full md:w-64 bg-white border-r border-gray-200 h-full flex flex-col">
            <div className="p-4 border-b border-gray-100">
                <div className="flex items-center justify-between gap-2">
                    <h2 className="font-bold text-gray-800">Salon</h2>
                    <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 text-[11px] font-semibold text-gray-700 hover:bg-gray-50"
                        onClick={() => setDirectModalOpen(true)}
                    >
                        <MessageSquarePlus size={14} />
                        個人チャット
                    </button>
                </div>
                <div className="mt-3 flex gap-2 text-xs">
                    <button
                        className={`px-3 py-1 rounded-full border ${
                            orderTab === 'fixed'
                                ? 'bg-gray-900 text-white border-gray-900'
                                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                        }`}
                        onClick={() => setOrderTab('fixed')}
                    >
                        固定順
                    </button>
                    <button
                        className={`px-3 py-1 rounded-full border ${
                            orderTab === 'recent'
                                ? 'bg-gray-900 text-white border-gray-900'
                                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                        }`}
                        onClick={() => setOrderTab('recent')}
                    >
                        新着順
                    </button>
                </div>
                {orderTab === 'fixed' && !isSearchActive && (
                    <div className="mt-1 text-[11px] text-gray-500">
                        ドラッグで順番変更可。ピン止めしたチャットは常に先頭側へ表示されます
                    </div>
                )}
                <div className="mt-3">
                    <input
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        placeholder="かな/カナ・自然文で検索"
                        className="w-full rounded border border-gray-200 px-3 py-2 text-xs text-gray-700 placeholder:text-gray-400 focus:border-gray-400 focus:outline-none"
                    />
                    {searching && <div className="mt-1 text-[11px] text-gray-400">検索中...</div>}
                </div>
            </div>

            <div className="flex-1 overflow-y-auto">
                {isSearchActive && !searching && orderedGroups.length === 0 && (
                    <div className="p-4 text-xs text-gray-400">一致するチャットがありません。</div>
                )}
                {orderedGroups.map((group) => {
                    const noti = groupNotifications[group.group_id] || { mentions: [], replies: [] };
                    const mentionCount = noti.mentions.length;
                    const replyCount = noti.replies.length;
                    const isMentionOpen = expandedMentions[group.group_id];
                    const isReplyOpen = expandedReplies[group.group_id];
                    const stat = groupStats[group.group_id] || {};
                    const unreadCount = Number(stat.unread_count || 0);
                    const showUnread = unreadCount > 0;
                    const unreadLabel = unreadCount > 99 ? '99+' : unreadCount;
                    const matchItems = searchMatchesByGroup[group.group_id] || [];
                    const canDrag = orderTab === 'fixed' && !isSearchActive && !group.is_deleted;
                    const isDragOver = dragOverId === group.group_id && draggingId !== null;
                    const isPinned = pinnedSet.has(group.group_id);
                    const groupType = String(group.group_type || 'normal').toLowerCase();
                    const subtitle = group.is_deleted
                        ? 'アクセス不可'
                        : groupType === 'direct'
                            ? '個人チャット'
                            : groupType === 'announcement'
                                ? 'アナウンス'
                                : '招待制';
                    return (
                        <div key={group.group_id} className="border-b border-gray-100">
                            <div
                                className={`p-3 flex items-center gap-3 ${
                                    group.is_deleted
                                        ? 'cursor-not-allowed opacity-60'
                                        : canDrag
                                            ? 'cursor-move'
                                            : 'cursor-pointer hover:bg-gray-50'
                                } ${selectedGroupId === group.group_id ? 'bg-yellow-50 border-r-2 border-yellow-500' : ''} ${
                                    isDragOver ? 'bg-yellow-50' : ''
                                }`}
                                draggable={canDrag}
                                onDragStart={(event) => handleDragStart(event, group.group_id)}
                                onDragOver={(event) => handleDragOver(event, group.group_id)}
                                onDrop={(event) => handleDrop(event, group.group_id)}
                                onDragEnd={handleDragEnd}
                                onClick={() => {
                                    if (!group.is_deleted) {
                                        onSelectGroup(group);
                                    }
                                }}
                            >
                                {group.is_direct && group.direct_peer_user_id ? (
                                    <button
                                        type="button"
                                        className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 overflow-hidden shrink-0 hover:ring-2 hover:ring-yellow-400"
                                        onClick={(event) => handleProfileNavigate(event, group.direct_peer_user_id)}
                                        aria-label={`${group.group_name} のプロフィールへ`}
                                        title="住人プロフィールを見る"
                                    >
                                        {group.icon_path ? (
                                            <img src={group.icon_path} alt={group.group_name || 'Resident icon'} className="w-full h-full object-cover" />
                                        ) : (
                                            <Users size={14} />
                                        )}
                                    </button>
                                ) : (
                                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 overflow-hidden shrink-0">
                                        {group.icon_path ? (
                                            <img src={group.icon_path} alt="Group icon" className="w-full h-full object-cover" />
                                        ) : (
                                            <Users size={14} />
                                        )}
                                    </div>
                                )}
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <div className="font-medium text-sm text-gray-900 truncate">
                                            {group.is_deleted ? `${group.group_name} は削除されました` : group.group_name}
                                        </div>
                                        {isPinned && <Pin size={12} className="text-[#800020] shrink-0 fill-current" />}
                                        {showUnread && (
                                            <span className="rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-semibold text-white shrink-0">
                                                {unreadLabel}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-500">{subtitle}</div>
                                    {isSearchActive && matchItems.length > 0 && (
                                        <div className="mt-2 space-y-1">
                                            {matchItems.slice(0, 2).map((item: any) => (
                                                <button
                                                    key={item.message_id}
                                                    className="w-full rounded border border-gray-100 bg-white px-2 py-1 text-left text-[11px] text-gray-600 hover:bg-gray-50"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        if (!group.is_deleted) {
                                                            onSelectGroup(group);
                                                            if (onJumpToMessage) {
                                                                onJumpToMessage(group.group_id, item.message_id);
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <span className="mr-2 font-semibold text-gray-700">
                                                        {resolveSearchName(item.sender_display_name, item.sender_room_no)}
                                                    </span>
                                                    <span className="line-clamp-1">{item.content}</span>
                                                </button>
                                            ))}
                                            {matchItems.length > 2 && (
                                                <div className="text-[10px] text-gray-400">他{matchItems.length - 2}件</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {!group.is_deleted && (
                                    <button
                                        type="button"
                                        className={`rounded-full border p-2 shrink-0 ${
                                            isPinned
                                                ? 'border-[#800020]/30 text-[#800020] bg-[#800020]/5'
                                                : 'border-gray-200 text-gray-400 hover:text-[#800020] hover:border-[#800020]/30'
                                        }`}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            togglePinForGroup(group.group_id, !isPinned);
                                        }}
                                        aria-label={isPinned ? 'ピンを外す' : 'ピン止めする'}
                                        title={isPinned ? 'ピンを外す' : 'ピン止めする'}
                                    >
                                        <Pin size={14} className={isPinned ? 'fill-current' : ''} />
                                    </button>
                                )}
                            </div>

                            {!group.is_deleted && mentionCount > 0 && (
                                <div className="px-3 pb-2 text-xs text-[#800020]">
                                    <button
                                        className="hover:underline"
                                        onClick={() => setExpandedMentions((prev) => ({ ...prev, [group.group_id]: !isMentionOpen }))}
                                    >
                                        {mentionCount}件メンションされています。
                                    </button>
                                    {isMentionOpen && (
                                        <div className="mt-2 space-y-2">
                                            {noti.mentions.map((item: any) => (
                                                <div key={item.notification_id} className="space-y-1">
                                                    <button
                                                        className="w-full text-left bg-white border rounded p-2 hover:bg-gray-50"
                                                        onClick={() => {
                                                            onSelectGroup(group);
                                                            if (onJumpToMessage) {
                                                                onJumpToMessage(group.group_id, item.message_id);
                                                            }
                                                        }}
                                                    >
                                                        <div className="font-semibold text-xs text-gray-700">
                                                            {item.sender_display_name?.trim() || '住人'}
                                                        </div>
                                                        <div className="text-xs text-gray-600 line-clamp-2">{item.content}</div>
                                                    </button>
                                                    <button
                                                        className="text-xs text-gray-600 underline"
                                                        onClick={() => handleMarkRead([item.notification_id])}
                                                    >
                                                        既読にする
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {!group.is_deleted && replyCount > 0 && (
                                <div className="px-3 pb-2 text-xs text-[#555]">
                                    <button
                                        className="hover:underline"
                                        onClick={() => setExpandedReplies((prev) => ({ ...prev, [group.group_id]: !isReplyOpen }))}
                                    >
                                        {replyCount}件リプライされています。
                                    </button>
                                    {isReplyOpen && (
                                        <div className="mt-2 space-y-2">
                                            {noti.replies.map((item: any) => (
                                                <div key={item.notification_id} className="space-y-1">
                                                    <button
                                                        className="w-full text-left bg-white border rounded p-2 hover:bg-gray-50"
                                                        onClick={() => {
                                                            onSelectGroup(group);
                                                            if (onJumpToMessage) {
                                                                onJumpToMessage(group.group_id, item.message_id);
                                                            }
                                                        }}
                                                    >
                                                        <div className="font-semibold text-xs text-gray-700">
                                                            {item.sender_display_name?.trim() || '住人'}
                                                        </div>
                                                        <div className="text-xs text-gray-600 line-clamp-2">{item.content}</div>
                                                    </button>
                                                    <button
                                                        className="text-xs text-gray-600 underline"
                                                        onClick={() => handleMarkRead([item.notification_id])}
                                                    >
                                                        既読にする
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {directModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                    <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
                        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                            <div>
                                <div className="text-sm font-semibold text-gray-900">個人チャットを始める</div>
                                <div className="text-xs text-gray-500">追加済みフレンドから選択します</div>
                            </div>
                            <button
                                type="button"
                                className="rounded-full border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50"
                                onClick={() => {
                                    setDirectModalOpen(false);
                                    setFriendSearch('');
                                }}
                            >
                                <X size={14} />
                            </button>
                        </div>
                        <div className="space-y-3 p-4">
                            <input
                                value={friendSearch}
                                onChange={(event) => setFriendSearch(event.target.value)}
                                placeholder="表示名 / RoomNo / ID で検索"
                                className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-gray-400 focus:outline-none"
                            />
                            <div className="max-h-[60vh] overflow-y-auto rounded-lg border border-gray-100">
                                {friendsLoading ? (
                                    <div className="p-4 text-sm text-gray-500">読み込み中...</div>
                                ) : filteredFriends.length === 0 ? (
                                    <div className="p-4 text-sm text-gray-500">選べるフレンドがいません。</div>
                                ) : (
                                    filteredFriends.map((friend) => {
                                        const isSalonEnabled = friend?.salon_enabled !== false;
                                        const isStarting = directStartingId === friend.user_id;
                                        return (
                                            <button
                                                key={friend.user_id}
                                                type="button"
                                                className={`flex w-full items-center gap-3 border-b border-gray-100 px-4 py-3 text-left last:border-b-0 ${
                                                    isSalonEnabled ? 'hover:bg-gray-50' : 'cursor-not-allowed bg-gray-50/60'
                                                }`}
                                                onClick={() => {
                                                    if (isSalonEnabled) {
                                                        handleOpenDirectChat(friend);
                                                    }
                                                }}
                                                disabled={!isSalonEnabled || isStarting || directStartingId !== null}
                                            >
                                                <span
                                                    className="h-10 w-10 overflow-hidden rounded-full bg-gray-200 shrink-0 cursor-pointer hover:ring-2 hover:ring-yellow-400"
                                                    onClick={(event) => handleProfileNavigate(event, friend.user_id)}
                                                    title="住人プロフィールを見る"
                                                >
                                                    {friend.avatar_path ? (
                                                        <img src={friend.avatar_path} alt={resolveDisplayName(friend)} className="h-full w-full object-cover" />
                                                    ) : (
                                                        <div className="flex h-full w-full items-center justify-center text-xs text-gray-500">
                                                            {resolveDisplayName(friend).slice(0, 1)}
                                                        </div>
                                                    )}
                                                </span>
                                                <div className="min-w-0 flex-1">
                                                    <div className="truncate text-sm font-medium text-gray-900">{resolveDisplayName(friend)}</div>
                                                    <div className="text-xs text-gray-500">
                                                        {friend.room_no ? `RoomNo.${friend.room_no}` : '部屋番号未設定'}
                                                        {isSalonEnabled ? '' : ' / サロン未対応'}
                                                    </div>
                                                </div>
                                                <div className="text-xs text-[#800020]">
                                                    {isStarting ? '作成中...' : isSalonEnabled ? '開始' : '不可'}
                                                </div>
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
